import React from 'react';
import { Zap, ArrowRight, Play } from 'lucide-react';

const features = [
  { icon: '⚡', title: 'Auto Publishing', desc: 'Schedule once, forget it. Your content goes live on TikTok at exactly the right time.' },
  { icon: '📅', title: 'Smart Calendar', desc: 'Visualize your entire content plan in one clean, intuitive view.' },
  { icon: '👥', title: 'Multi-Account', desc: 'Manage all your TikTok accounts from a single dashboard.' },
  { icon: '🔒', title: 'Secure OAuth', desc: 'We never store your TikTok password. We use official TikTok OAuth.' },
];

export default function LandingPage({ onAuth }) {
  return (
    <div style={s.page}>
      {/* Nav */}
      <nav style={s.nav}>
        <div style={s.navLogo}>
          <div style={s.logoIcon}><Zap size={18} color="#fff" fill="#fff" /></div>
          <span style={s.logoText}>VidFlow</span>
        </div>
        <div style={s.navActions}>
          <button style={s.loginBtn} onClick={() => onAuth('login')}>Log In</button>
          <button style={s.signupBtn} onClick={() => onAuth('register')}>Get Started Free</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={s.hero}>
        <div style={s.heroBadge}>
          <span style={s.heroBadgeDot} />
          The easiest way to manage TikTok content
        </div>
        <h1 style={s.heroTitle}>
          Schedule your content,<br />
          <span style={s.heroAccent}>TikTok handles the rest.</span>
        </h1>
        <p style={s.heroSub}>
          Upload your video, pick a publish time — VidFlow automatically sends it to TikTok when the moment arrives.
          Manage multiple accounts from one place.
        </p>
        <div style={s.heroCtas}>
          <button style={s.ctaPrimary} onClick={() => onAuth('register')}>
            Start for Free <ArrowRight size={18} />
          </button>
          <button style={s.ctaSecondary}>
            <Play size={16} fill="currentColor" /> Watch Demo
          </button>
        </div>
      </section>

      {/* App preview */}
      <div style={s.previewWrapper}>
        <div style={s.previewCard}>
          <div style={s.previewBar}>
            <span style={s.dot1} /><span style={s.dot2} /><span style={s.dot3} />
            <span style={s.previewUrl}>contentstream.app/dashboard</span>
          </div>
          <div style={s.previewBody}>
            <div style={s.previewSidebar}>
              {['Dashboard', 'Upload', 'Schedule', 'Queue'].map(item => (
                <div key={item} style={s.previewNavItem}>{item}</div>
              ))}
            </div>
            <div style={s.previewContent}>
              <div style={s.previewStatRow}>
                {['1.28M views', '94K likes', '3 scheduled'].map(stat => (
                  <div key={stat} style={s.previewStat}>{stat}</div>
                ))}
              </div>
              <div style={s.previewVideoList}>
                {['Morning Routine ✨', 'Coding Tips 🔥', 'Travel Vlog 🌴'].map(title => (
                  <div key={title} style={s.previewVideoItem}>
                    <div style={s.previewThumb} />
                    <div style={s.previewVideoInfo}>
                      <div style={s.previewLine1} />
                      <div style={s.previewLine2} />
                    </div>
                    <div style={s.previewBadge}>Scheduled</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div style={s.previewGlow} />
      </div>

      {/* Features */}
      <section style={s.features}>
        <h2 style={s.featuresTitle}>Why VidFlow?</h2>
        <div style={s.featuresGrid}>
          {features.map(f => (
            <div key={f.title} style={s.featureCard}>
              <div style={s.featureIcon}>{f.icon}</div>
              <h3 style={s.featureTitle}>{f.title}</h3>
              <p style={s.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA bottom */}
      <section style={s.ctaSection}>
        <h2 style={s.ctaTitle}>Ready to get started?</h2>
        <p style={s.ctaSub}>Create a free account, connect your TikTok, and start scheduling.</p>
        <button style={s.ctaPrimary2} onClick={() => onAuth('register')}>
          Create Free Account <ArrowRight size={18} />
        </button>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <div style={s.footerLogo}>
          <div style={s.logoIconSm}><Zap size={14} color="#fff" fill="#fff" /></div>
          <span style={s.logoTextSm}>VidFlow</span>
        </div>
        <div style={s.footerLinks}>
          <a style={s.footerLink} href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span style={s.footerDot}>·</span>
          <a style={s.footerLink} href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        </div>
        <p style={s.footerText}>© 2026 VidFlow. All rights reserved.</p>
      </footer>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', overflowX: 'hidden' },
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 60px', borderBottom: '1px solid var(--border)',
    position: 'sticky', top: 0,
    background: 'rgba(10,10,15,0.85)', backdropFilter: 'blur(12px)', zIndex: 100,
  },
  navLogo: { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon: {
    width: 32, height: 32, borderRadius: 9,
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 18px var(--accent-glow)',
  },
  logoIconSm: {
    width: 26, height: 26, borderRadius: 7,
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  logoText: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' },
  logoTextSm: { fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)' },
  navActions: { display: 'flex', alignItems: 'center', gap: 12 },
  loginBtn: {
    padding: '8px 20px', background: 'transparent',
    border: '1px solid var(--border)', borderRadius: 9,
    color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  signupBtn: {
    padding: '8px 20px',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    border: 'none', borderRadius: 9,
    color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    boxShadow: '0 0 18px var(--accent-glow)',
  },
  hero: {
    textAlign: 'center', padding: '90px 24px 60px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
  },
  heroBadge: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, fontWeight: 600, color: 'var(--accent)',
    background: 'var(--accent-light)', padding: '6px 16px', borderRadius: 20,
    border: '1px solid rgba(108,99,255,0.3)',
  },
  heroBadgeDot: {
    width: 7, height: 7, borderRadius: '50%',
    background: 'var(--accent)', boxShadow: '0 0 6px var(--accent)',
  },
  heroTitle: {
    fontSize: 62, fontWeight: 800, color: 'var(--text-primary)',
    lineHeight: 1.1, letterSpacing: '-2px', maxWidth: 700,
  },
  heroAccent: {
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  heroSub: { fontSize: 18, color: 'var(--text-secondary)', maxWidth: 520, lineHeight: 1.6 },
  heroCtas: { display: 'flex', gap: 12, marginTop: 8 },
  ctaPrimary: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '14px 28px',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 32px var(--accent-glow)',
  },
  ctaSecondary: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '14px 28px', background: 'var(--bg-card)',
    color: 'var(--text-secondary)', border: '1px solid var(--border)',
    borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  previewWrapper: {
    position: 'relative', display: 'flex',
    justifyContent: 'center', padding: '0 40px 80px',
  },
  previewCard: {
    width: '100%', maxWidth: 860,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 18, overflow: 'hidden',
    boxShadow: '0 40px 80px rgba(0,0,0,0.6)',
    position: 'relative', zIndex: 1,
  },
  previewGlow: {
    position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)',
    width: 600, height: 100,
    background: 'radial-gradient(ellipse, rgba(108,99,255,0.25), transparent)',
    filter: 'blur(30px)',
  },
  previewBar: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', background: 'var(--bg-secondary)',
    borderBottom: '1px solid var(--border)',
  },
  dot1: { width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' },
  dot2: { width: 10, height: 10, borderRadius: '50%', background: '#febc2e' },
  dot3: { width: 10, height: 10, borderRadius: '50%', background: '#28c840' },
  previewUrl: { fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 },
  previewBody: { display: 'flex', height: 200 },
  previewSidebar: {
    width: 130, background: 'var(--bg-secondary)',
    borderRight: '1px solid var(--border)',
    padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 4,
  },
  previewNavItem: { padding: '7px 10px', fontSize: 11, color: 'var(--text-muted)', borderRadius: 6 },
  previewContent: { flex: 1, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  previewStatRow: { display: 'flex', gap: 8 },
  previewStat: {
    flex: 1, padding: '8px 10px', fontSize: 10, color: 'var(--text-secondary)',
    background: 'var(--bg-secondary)', borderRadius: 8, textAlign: 'center', fontWeight: 600,
  },
  previewVideoList: { display: 'flex', flexDirection: 'column', gap: 6 },
  previewVideoItem: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 8,
  },
  previewThumb: { width: 28, height: 28, borderRadius: 6, background: 'var(--border)', flexShrink: 0 },
  previewVideoInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  previewLine1: { height: 6, background: 'var(--border)', borderRadius: 3, width: '60%' },
  previewLine2: { height: 5, background: 'var(--bg-card)', borderRadius: 3, width: '40%' },
  previewBadge: { fontSize: 9, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-light)', padding: '2px 7px', borderRadius: 20 },
  features: { padding: '60px 60px', textAlign: 'center' },
  featuresTitle: { fontSize: 36, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 40, letterSpacing: '-1px' },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 },
  featureCard: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)', padding: '28px 20px', textAlign: 'left',
  },
  featureIcon: { fontSize: 28, marginBottom: 12 },
  featureTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 },
  featureDesc: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 },
  ctaSection: {
    textAlign: 'center', padding: '60px 24px 80px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
  },
  ctaTitle: { fontSize: 38, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-1px' },
  ctaSub: { fontSize: 16, color: 'var(--text-secondary)' },
  ctaPrimary2: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '16px 36px', marginTop: 8,
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 14,
    fontSize: 16, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 40px var(--accent-glow)',
  },
  footer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    padding: '28px', borderTop: '1px solid var(--border)',
  },
  footerLogo: { display: 'flex', alignItems: 'center', gap: 8 },
  footerLinks: { display: 'flex', alignItems: 'center', gap: 8 },
  footerLink: {
    fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500,
    textDecoration: 'none',
  },
  footerDot: { fontSize: 13, color: 'var(--text-muted)' },
  footerText: { fontSize: 12, color: 'var(--text-muted)' },
};
